import React, { Component } from "react";
import { PricingPayload } from "../../../stores/PricingStore/Types";
import { priceLineFor } from "../priceLine";
import { AxiosResponse, AxiosError } from "axios";
import {
  CoursePreviewResource,
  QuotationResource,
} from "../../../stores/CoursePreviewStore/Types";
import { CurrentUserResource } from "../../../stores/CurrentUserStore/Types";
import { postRequest } from "../../../utils/httpRequest";
import CreditsUsedConfirmation from "./CreditsUsedConfirmation";
import RestrictedEnrolmentConfirmation from "./RestrictedEnrolmentConfirmation";
import BrochureDownload from "./BrochureDownload";
import { pushServerDataLayer } from "../../../utils/analytics";

interface Props {
  mode?: string;
  refresh: (slug: string) => void;
  course: CoursePreviewResource;
  showSigninSidebar: boolean;
  loadingQuotations: boolean;
  currentUser: CurrentUserResource;
  quotationResource: QuotationResource;
  loadingEnrolmentInfo: boolean;
  enrolmentInfo: {
    existing_subscription: boolean;
    enrolment_id: number;
  };
  handleShowSignInSideBar: (redirectUrl?: string) => void;
  pricingState?: { kind: string; pricing?: PricingPayload };
}

interface State {
  enrolingUser: boolean;
  canEnrolWithRestrictedSub: boolean;
  isCreditsUsedUp: boolean;
  firstName: string;
  email: string;
  featureInterestSubmitting: boolean;
  featureInterestSubmitted: boolean;
  featureInterestError: string;
  brochureOpen: boolean;
}

// A bare href="#section" does not work on this page. Hash navigation remounts
// CoursePreview, and its componentDidMount scrolls back to 0, so the reader
// lands exactly where they started. Scroll to the section directly and leave
// the URL alone. The href stays for keyboard, middle-click and no-JS.
const scrollToSection = (
  event: React.MouseEvent<HTMLAnchorElement>,
  id: string
) => {
  // Hand modified and non-primary clicks back to the browser, so cmd-click and
  // middle-click still open the section in a new tab rather than being
  // swallowed by preventDefault below.
  const modified =
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey;
  if (modified) return;

  const target = document.getElementById(id);
  if (!target) return;

  event.preventDefault();

  const prefersReducedMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  )?.matches;

  target.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "start",
  });
};

const RatingStars: React.FC<{ rating: number }> = ({ rating }) => {
  // Fill the stars proportionally rather than rounding to whole ones. Rounding
  // was invisible while every star was the same grey, but once they are
  // coloured in a 4.8 would read as a flawless 5.
  const value = Math.min(Math.max(Number(rating) || 0, 0), 5);
  const fillWidth = `${(value / 5) * 100}%`;

  return (
    <>
      {/* Decorative: the numeric rating sits directly above this. */}
      <span className="hero-stars" aria-hidden="true">
        ★★★★★
        <span className="hero-stars-fill" style={{ width: fillWidth }}>
          ★★★★★
        </span>
      </span>
      <span className="statbar-label">Rating</span>
    </>
  );
};

const RatingStat: React.FC<{ rating: number }> = ({ rating }) => (
  <>
    <strong>{rating?.toFixed(1)}</strong>
    <span className="hero-rating-stars">
      <RatingStars rating={rating} />
    </span>
  </>
);

class Landing extends Component<Props, State> {
  constructor(props) {
    super(props);

    const { course, currentUser } = props;

    this.state = {
      enrolingUser: false,
      canEnrolWithRestrictedSub: false,
      isCreditsUsedUp: false,
      firstName:
        currentUser?.kind === "current-user" ? currentUser.name || "" : "",
      email:
        currentUser?.kind === "current-user" ? currentUser.email || "" : "",
      featureInterestSubmitting: false,
      featureInterestSubmitted: course?.feature_interest || false,
      featureInterestError: "",
      brochureOpen: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.course?.id !== this.props.course?.id) {
      this.setState({
        featureInterestSubmitted: this.props.course?.feature_interest || false,
        featureInterestError: "",
      });
    }
  }

  subscriptionOnlyButton = () => (
    <button
      className="ui traditional button fluid add-to-cart-button"
      style={{ marginTop: "1em" }}
    >
      <i className="lock icon"></i>
      Subscriber Only
    </button>
  );

  toggleRestrictedConfirmation = () => {
    this.setState({
      canEnrolWithRestrictedSub: !this.state.canEnrolWithRestrictedSub,
    });
  };

  toggleIsCreditsUsedUp = () => {
    this.setState({ isCreditsUsedUp: !this.state.isCreditsUsedUp });
  };

  startCourse = (e) => {
    const { plan_slug, merchant_quotation_id, new_enrolment_url } =
      this.props.quotationResource;

    e.preventDefault();

    this.setState({ enrolingUser: true });

    return postRequest(`${new_enrolment_url}.json`, {
      plan_slug,
      merchant_quotation_id,
    })
      .then((response: AxiosResponse<{ url: string; datalayer?: any }>) => {
        pushServerDataLayer(response.data.datalayer);
        (window as any).location = response.data.url;
      })
      .catch((error: AxiosError<{ url: string }>) => {
        this.setState({ enrolingUser: false });
      });
  };

  handleFeatureInterestChange = (field: "firstName" | "email") => (e) => {
    this.setState({
      [field]: e.target.value,
      featureInterestError: "",
    } as Pick<State, keyof State>);
  };

  submitFeatureInterest = (e) => {
    e.preventDefault();

    const { course } = this.props;
    const { firstName, email, featureInterestSubmitting } = this.state;

    if (featureInterestSubmitting) return;

    if (!email.trim()) {
      this.setState({
        featureInterestError:
          "Pop in your email so we know where to send the launch note.",
      });
      return;
    }

    this.setState({
      featureInterestSubmitting: true,
      featureInterestError: "",
    });

    return postRequest(`/api/feature_interests`, {
      course_slug: course.slug,
      first_name: firstName,
      email,
    })
      .then(() => {
        this.setState({
          featureInterestSubmitting: false,
          featureInterestSubmitted: true,
          featureInterestError: "",
        });
      })
      .catch((error: AxiosError<{ errors: string }>) => {
        this.setState({
          featureInterestSubmitting: false,
          featureInterestError:
            error.response?.data?.errors ||
            "Something went sideways. Please try again in a moment.",
        });
      });
  };

  formatLaunchWindow = () => {
    const { release_date } = this.props.course;

    if (!release_date) return null;

    const date = new Date(release_date);
    if (Number.isNaN(date.getTime())) return null;

    const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
    return `Q${quarter} ${date.getUTCFullYear()}`;
  };

  renderUpcomingForm = () => {
    const {
      firstName,
      email,
      featureInterestSubmitting,
      featureInterestSubmitted,
      featureInterestError,
    } = this.state;

    if (featureInterestSubmitted) {
      return (
        <div className="launch-success" role="status">
          <strong>You're on the list.</strong>
          <span>We'll email you the moment this course opens.</span>
        </div>
      );
    }

    return (
      <form className="launch-form" onSubmit={this.submitFeatureInterest}>
        <div className="launch-form-fields">
          <label>
            <span className="sr-only">First name</span>
            <input
              type="text"
              name="first_name"
              placeholder="First name"
              value={firstName}
              onChange={this.handleFeatureInterestChange("firstName")}
              autoComplete="given-name"
            />
          </label>
          <label>
            <span className="sr-only">Email address</span>
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={email}
              onChange={this.handleFeatureInterestChange("email")}
              autoComplete="email"
              required
            />
          </label>
        </div>
        {featureInterestError && (
          <p className="launch-form-error" role="alert">
            {featureInterestError}
          </p>
        )}
        <button
          type="submit"
          className={`btn btn-blue ${featureInterestSubmitting ? "loading" : ""}`}
          disabled={featureInterestSubmitting}
        >
          Be first to know
        </button>
      </form>
    );
  };

  renderActionButton = () => {
    const { currentUser, course, enrolmentInfo } = this.props;

    const { enrolingUser } = this.state;

    if (enrolmentInfo?.enrolment_id) {
      return (
        <a
          href={`/enrolments/${enrolmentInfo.enrolment_id}`}
          className="btn btn-blue"
        >
          Continue
        </a>
      );
    } else if (currentUser.kind === "current-user") {
      if (enrolmentInfo?.existing_subscription || course.sample) {
        if (course.is_restricted_subscription && course.available_credits < 1) {
          return (
            <button
              className={`btn btn-blue ${enrolingUser ? "loading" : ""}`}
              disabled={enrolingUser}
              onClick={this.toggleIsCreditsUsedUp}
              type="button"
            >
              <i className="student icon"></i>Start now
            </button>
          );
        } else if (
          course.is_restricted_subscription &&
          course.available_credits > 0
        ) {
          return (
            <button
              className={`btn btn-blue ${enrolingUser ? "loading" : ""}`}
              disabled={enrolingUser}
              onClick={this.toggleRestrictedConfirmation}
              type="button"
            >
              <i className="student icon"></i>Start now
            </button>
          );
        } else {
          return (
            <button
              className={`btn btn-blue ${enrolingUser ? "loading" : ""}`}
              disabled={enrolingUser}
              onClick={this.startCourse}
              type="button"
            >
              <i className="student icon"></i>Start now
            </button>
          );
        }
      } else {
        if (course.subscription_only && !enrolmentInfo?.existing_subscription) {
          return this.subscriptionOnlyButton();
        } else {
          return (
            <a className="btn btn-blue" href={`/courses/${course.slug}/pay`}>
              Enrol now
            </a>
          );
        }
      }
    } else if (
      course.subscription_only &&
      !enrolmentInfo.existing_subscription
    ) {
      return this.subscriptionOnlyButton();
    } else if (course.sample) {
      return (
        <a href={`/courses/${course.slug}/pay`} className="btn btn-blue">
          Enrol now
        </a>
      );
    } else {
      return (
        <a className="btn btn-blue" href={`/courses/${course.slug}/pay`}>
          Enrol now
        </a>
      );
    }
  };
  // A quiet link under the price, for people who want to read it later or
  // pass it to whoever signs off the training budget.
  renderBrochureLink = () => {
    const { course, currentUser } = this.props;

    return (
      <>
        <button
          type="button"
          className="brochure-link"
          onClick={() => this.setState({ brochureOpen: true })}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3v12m0 0-5-5m5 5 5-5M4 19h16" />
          </svg>
          Download the course brochure
        </button>
        <BrochureDownload
          open={this.state.brochureOpen}
          onClose={() => this.setState({ brochureOpen: false })}
          courseName={course.name}
          courseSlug={course.slug}
          signedInEmail={currentUser?.kind === "current-user" ? currentUser.email : undefined}
        />
      </>
    );
  };

  // The price, in the visitor's own currency and at their own tier, exactly
  // as the pricing section lower down says it. Nothing for a free sample
  // course or one that has not launched.
  renderHeroPrice = () => {
    const { course, mode, pricingState } = this.props;
    if (course?.sample || mode === "upcoming") return null;
    if (!pricingState || pricingState.kind !== "ready") return null;

    const line = priceLineFor(pricingState.pricing);
    if (!line) return null;

    return (
      <p className="hero-price">
        {line.prefix && <>{line.prefix} </>}
        <b>
          {line.symbol}
          {line.amount}
        </b>
        {line.code && <span className="hero-price-code"> {line.code}</span>}
        {line.alternative && (
          <span className="hero-price-alt">{line.alternative}</span>
        )}
      </p>
    );
  };

  render() {
    const { course, currentUser, mode } = this.props;
    const { canEnrolWithRestrictedSub, isCreditsUsedUp, enrolingUser } =
      this.state;

    const backgroundImage =
      currentUser.is_in_uk && course?.preview_masthead_alt_image_url
        ? course?.preview_masthead_alt_image_url
        : course?.preview_masthead_image_url;
    const isUpcoming = mode === "upcoming";
    const hasLessons = (course?.total_questions_count || 0) > 0;
    const launchWindow = this.formatLaunchWindow();

    return (
      <>
        <RestrictedEnrolmentConfirmation
          isOpen={canEnrolWithRestrictedSub}
          onClose={this.toggleRestrictedConfirmation}
          onConfirm={this.startCourse}
          availableCredits={course.available_credits}
          enrolingUser={enrolingUser}
        />
        <CreditsUsedConfirmation
          isOpen={isCreditsUsedUp}
          onClose={this.toggleIsCreditsUsedUp}
        />
        <section
          className={`hero${isUpcoming ? " upcoming-hero" : ""}`}
          aria-labelledby="course-title"
          id="launch-signup"
        >
          <div className="hero-bg" aria-hidden="true">
            {backgroundImage ? (
              <div className="hero-bg-image">
                <img
                  src={backgroundImage}
                  alt=""
                  loading="eager"
                  decoding="async"
                  width="1400"
                  height="575"
                />
              </div>
            ) : (
              <div className="hero-bg-fallback" />
            )}
            <div className="hero-bg-tint"></div>
          </div>

          <div className="container">
            <div className="hero-split">
              <div className="hero-split-left">
                {isUpcoming && <span className="launch-pill">Coming soon</span>}
                <h1 id="course-title">{course?.name}</h1>
              </div>

              <div className="hero-split-right">
                <div className="hero-rcard">
                  <p className="lede">{course?.preview_mini_description}</p>

                  {isUpcoming ? (
                    this.renderUpcomingForm()
                  ) : (
                    <div className="cta-row">
                      {this.renderActionButton()}
                      <a
                        href="#chapters"
                        className="btn btn-white"
                        onClick={(event) => scrollToSection(event, "chapters")}
                      >
                        See what's inside
                      </a>
                    </div>
                  )}

                  {this.renderHeroPrice()}
                  {!isUpcoming && this.renderBrochureLink()}

                  <div className="reassure" aria-label="Money back guarantee">
                    <svg aria-hidden="true">
                      <use href="#ic-shield" />
                    </svg>
                    <span>
                      {isUpcoming
                        ? "One email when it launches. That's it."
                        : "30-day money back guaranteed"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="hero-statbar"
            role="list"
            aria-label="Course at a glance"
          >
            <div className="container">
              {(!isUpcoming || hasLessons) && (
                <>
                  <div role="listitem">
                    <a
                      className="statbar-link"
                      href="#chapters"
                      onClick={(event) => scrollToSection(event, "chapters")}
                      aria-label={`${course?.total_questions_count} lessons — see the course chapters`}
                    >
                      <strong>{course?.total_questions_count}</strong>
                      <span className="statbar-label">Lessons</span>
                    </a>
                  </div>
                  <div role="listitem">
                    {course.sample ? (
                      <strong>~1h</strong>
                    ) : (
                      <strong>
                        ~{course.learning_hours ? course.learning_hours : 9} h
                      </strong>
                    )}
                    <span>Of content</span>
                  </div>
                </>
              )}
              {!isUpcoming && (
                <div role="listitem">
                  {/* Reviews unmounts itself when a course has no reviews, so
                      there is nothing to scroll to without a rating. */}
                  {course?.rating ? (
                    <a
                      className="statbar-link"
                      href="#course-reviews"
                      onClick={(event) =>
                        scrollToSection(event, "course-reviews")
                      }
                      aria-label={`Rated ${course.rating.toFixed(
                        1
                      )} out of 5 — read learner reviews`}
                    >
                      <RatingStat rating={course.rating} />
                    </a>
                  ) : (
                    <RatingStat rating={course?.rating} />
                  )}
                </div>
              )}
              {isUpcoming && launchWindow && (
                <div role="listitem">
                  <strong>{launchWindow}</strong>
                  <span>Expected launch</span>
                </div>
              )}
              <div role="listitem">
                <strong>Mini MBA</strong>
                <span>CPD-accredited</span>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}

export default Landing;
